import { 
  Brain, 
  Calendar, 
  Clock, 
  Users, 
  Smartphone, 
  Trophy,
  Settings,
  Shield,
  Lock,
  Server,
  CheckCircle,
  Globe,
  TrendingUp,
  Zap,
  BarChart3,
  Target,
  Activity,
  Bell,
  MessageSquare,
  Headphones,
  FileText,
  RefreshCw,
  Timer,
  UserCheck,
  Layers,
  Database,
  Eye,
  Wifi,
  GitBranch
} from 'lucide-react';
import type { ProductFeatureConfig } from './types';

export const workforceManagementSystemConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Brain,
      title: 'AI-Powered Demand Forecasting',
      description: 'Predict call volumes, chat demand, and staffing needs with up to 98% accuracy using advanced machine learning that analyses historical patterns, seasonal trends, and external factors.',
      metrics: '98% accuracy',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: Calendar,
      title: 'Intelligent Schedule Optimisation',
      description: 'Balance business requirements with agent preferences through AI-optimised scheduling that considers skills, compliance rules, labour laws, and shift preferences automatically.',
      metrics: '40% admin savings',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      icon: Clock,
      title: 'Real-Time Adherence Monitoring',
      description: 'Monitor agent activity against schedules in real-time with automated variance alerts, instant supervisor notifications, and historical adherence trending.',
      metrics: '96% adherence',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: Smartphone,
      title: 'Mobile Self-Service Portal',
      description: 'Empower agents with native iOS and Android apps for schedule access, shift swaps, time-off requests, personal performance dashboards, and push notifications.',
      metrics: '62% retention lift',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      icon: Trophy,
      title: 'Gamification & Engagement',
      description: 'Transform KPIs into competitive tournaments with leaderboards, achievement badges, peer recognition, team challenges, and rewards marketplace integration.',
      metrics: '28% performance gain',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: Settings,
      title: 'Enterprise Integration Hub',
      description: 'Connect with 200+ pre-built integrations including HRIS (Workday, SAP), payroll (ADP, Kronos), telephony (Genesys, Avaya, Five9), and collaboration tools (Teams, Slack).',
      metrics: '200+ integrations',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: TrendingUp,
      title: 'Multi-Channel Volume Forecasting',
      description: 'Predict volumes across voice, chat, email, social media, and video with pattern analysis, seasonal adjustments, and marketing campaign impact modelling.',
      metrics: 'All channels covered',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: Activity,
      title: 'Intraday Performance Management',
      description: 'React to real-time conditions with dynamic schedule adjustments, automated reforecasting, overtime offers, and voluntary time-off management.',
      metrics: 'Real-time response',
      color: 'from-rose-500 to-red-500'
    },
    {
      icon: Headphones,
      title: 'Omnichannel Skill Routing',
      description: 'Match agents to interactions based on 100+ skill dimensions, proficiency levels, language capabilities, and customer preferences for optimal outcomes.',
      metrics: '32% CSAT improvement',
      color: 'from-red-500 to-orange-500'
    },
    {
      icon: Bell,
      title: 'Automated Alerts & Notifications',
      description: 'Configurable alert engine for schedule exceptions, adherence breaches, service level risks, overtime thresholds, and compliance violations.',
      metrics: 'Instant alerts',
      color: 'from-orange-500 to-amber-500'
    },
    {
      icon: Users,
      title: 'Multi-Site Workforce Coordination',
      description: 'Unified scheduling and forecasting across multiple locations, regions, and outsourcing partners with timezone management and load balancing.',
      metrics: 'Global coverage',
      color: 'from-amber-500 to-yellow-500'
    },
    {
      icon: FileText,
      title: 'Compliance & Labour Law Engine',
      description: 'Built-in compliance rules for working time directives, break requirements, overtime limits, union agreements, and regional labour regulations.',
      metrics: '100% compliant',
      color: 'from-yellow-500 to-lime-500'
    }
  ],
  advancedCapabilities: [
    {
      icon: Target,
      title: 'AI Skill-Based Routing Optimisation',
      description: 'Machine learning continuously optimises routing rules based on handle time, resolution rates, and customer satisfaction to maximise first-contact resolution.',
      metrics: 'Smart matching',
      color: 'from-teal-500 to-emerald-500'
    },
    {
      icon: Zap,
      title: 'Dynamic Intraday Reforecasting',
      description: 'Real-time demand signals automatically trigger schedule adjustments with agent notifications for overtime, early release, or cross-skill assignments.',
      metrics: 'Adaptive planning',
      color: 'from-cyan-500 to-teal-500'
    },
    {
      icon: BarChart3,
      title: 'Executive Analytics Dashboard',
      description: 'Comprehensive dashboards with coaching insights, trend analysis, team scorecards, cost-per-contact metrics, and ROI tracking for workforce investments.',
      metrics: 'Full visibility',
      color: 'from-indigo-500 to-blue-500'
    },
    {
      icon: Globe,
      title: 'HRIS & Payroll Synchronisation',
      description: 'Bi-directional integration with Workday, SAP SuccessFactors, ADP, Kronos, and Oracle HCM for automated data synchronisation and time export.',
      metrics: 'Auto-sync enabled',
      color: 'from-purple-500 to-indigo-500'
    },
    {
      icon: MessageSquare,
      title: 'Agent Communication Hub',
      description: 'In-app messaging for supervisors and agents with shift announcements, team updates, schedule change notifications, and acknowledgement tracking.',
      metrics: 'Instant comms',
      color: 'from-pink-500 to-purple-500'
    },
    {
      icon: RefreshCw,
      title: 'Automated Shift Swap Marketplace',
      description: 'Agent-driven shift trading with rule-based approval workflows ensuring skill coverage, labour law compliance, and fair distribution.',
      metrics: 'Self-service swaps',
      color: 'from-rose-500 to-pink-500'
    },
    {
      icon: Timer,
      title: 'Long-Term Capacity Planning',
      description: 'Strategic workforce planning with hiring forecasts, attrition modelling, training pipeline analysis, and budget scenario planning.',
      metrics: '12-month horizon',
      color: 'from-red-500 to-rose-500'
    },
    {
      icon: UserCheck,
      title: 'Agent Performance Coaching',
      description: 'AI-driven coaching recommendations based on adherence patterns, performance trends, and skill gaps with supervisor action tracking.',
      metrics: 'Targeted coaching',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Layers,
      title: 'Multi-Tenant Architecture',
      description: 'Support for BPO environments with client-specific configurations, branding, data segregation, and custom reporting hierarchies.',
      metrics: 'BPO-ready',
      color: 'from-amber-500 to-orange-500'
    }
  ],
  securityPerformance: [
    {
      icon: Shield,
      title: 'SOC 2 Type II Certified',
      description: 'Enterprise-grade security with annual third-party audits, comprehensive controls, and detailed compliance documentation.',
      metrics: 'Certified secure',
      color: 'from-emerald-600 to-teal-600'
    },
    {
      icon: Lock,
      title: 'ISO 27001 Compliant',
      description: 'Information security management system meeting international standards with continuous monitoring and improvement.',
      metrics: 'ISO certified',
      color: 'from-teal-600 to-cyan-600'
    },
    {
      icon: CheckCircle,
      title: 'GDPR & Data Protection',
      description: 'Built-in privacy controls, data subject rights management, consent tracking, and automated data retention policies.',
      metrics: 'Full compliance',
      color: 'from-cyan-600 to-blue-600'
    },
    {
      icon: Server,
      title: '99.99% Uptime SLA',
      description: 'High-availability infrastructure with automatic failover, geographic redundancy, and real-time health monitoring.',
      metrics: '99.99% uptime',
      color: 'from-blue-600 to-indigo-600'
    },
    {
      icon: Eye,
      title: 'Role-Based Access Control',
      description: 'Granular permissions for agents, team leads, schedulers, forecasters, supervisors, and administrators with audit trails.',
      metrics: 'Fine-grained control',
      color: 'from-indigo-600 to-purple-600'
    },
    {
      icon: Database,
      title: 'Data Encryption at Rest & Transit',
      description: 'AES-256 encryption for all stored data with TLS 1.3 for data in transit and customer-managed encryption keys option.',
      metrics: 'Military-grade',
      color: 'from-purple-600 to-pink-600'
    },
    {
      icon: Wifi,
      title: 'Flexible Deployment Options',
      description: 'Cloud, hybrid, or on-premises deployment options with dedicated tenancy available for regulated industries.',
      metrics: 'Your choice',
      color: 'from-pink-600 to-rose-600'
    },
    {
      icon: GitBranch,
      title: 'Disaster Recovery & Backup',
      description: 'Automated backups with point-in-time recovery, cross-region replication, and documented DR procedures with RTO/RPO guarantees.',
      metrics: '< 1hr RTO',
      color: 'from-rose-600 to-red-600'
    },
    {
      icon: Users,
      title: 'SSO & Enterprise Authentication',
      description: 'SAML 2.0, OAuth 2.0, and OpenID Connect support with integration for Okta, Azure AD, Ping Identity, and custom IdPs.',
      metrics: 'Enterprise SSO',
      color: 'from-red-600 to-orange-600'
    }
  ]
};
